import React, { useEffect, useState } from "react";
import { PackageX, HandCoins, ShoppingBag, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { adminApi, inr, errorMessage } from "../../lib/api";

const RANGES = [7, 30, 90];

function resolutionBadge(r) {
  if (r === "self_pickup") return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="res-self-pickup"><ShoppingBag className="h-3 w-3" /> Self-pickup</span>;
  if (r === "refund") return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700" data-testid="res-refund"><HandCoins className="h-3 w-3" /> Refunded</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="res-waiting"><Clock className="h-3 w-3" /> Still waiting</span>;
}

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

export default function NoPartnerReport() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = (d) => {
    setLoading(true);
    adminApi.noPartnerReport(d).then(setData).catch((e) => { toast.error(errorMessage(e)); setData({ items: [], counts: {} }); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <>
      <PageHeader title="No-driver orders" description="Food orders a restaurant accepted but no delivery partner picked up — and how each one was resolved." />

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="np-range">
        {RANGES.map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`np-range-${d}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${days === d ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:text-ink"}`}>Last {d} days</button>
        ))}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />}
      </div>

      {data === null ? <LoadingBlock rows={4} /> : items.length === 0 ? (
        <EmptyState icon={PackageX} title="No no-driver orders" text="When a restaurant-accepted order can't find a delivery partner, it shows up here with its outcome." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="np-summary">
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Total</p><p className="text-2xl font-bold text-ink">{data.count}</p></div>
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Self-pickup</p><p className="text-2xl font-bold text-emerald-600">{counts.self_pickup || 0}</p></div>
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Refunded</p><p className="text-2xl font-bold text-red-600">{counts.refund || 0}</p></div>
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Still waiting</p><p className="text-2xl font-bold text-amber-600">{counts.waiting || 0}</p></div>
          </div>

          <div className="kt-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="np-table">
              <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-5 py-3 font-semibold">Order</th>
                  <th className="px-5 py-3 font-semibold">Restaurant</th>
                  <th className="hidden px-5 py-3 font-semibold md:table-cell">Customer</th>
                  <th className="px-5 py-3 font-semibold">Total</th>
                  <th className="hidden px-5 py-3 font-semibold sm:table-cell">No partner at</th>
                  <th className="px-5 py-3 font-semibold">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                {items.map((it) => (
                  <tr key={it.order_id} className="hover:bg-surface-50" data-testid={`np-row-${it.order_id}`}>
                    <td className="px-5 py-4 font-mono text-xs font-semibold text-ink">#{(it.order_id || "").slice(-6).toUpperCase()}</td>
                    <td className="px-5 py-4 text-ink">{it.restaurant_name || "—"}</td>
                    <td className="hidden px-5 py-4 text-ink-soft md:table-cell">
                      <p>{it.customer_name || "—"}</p>
                      {it.customer_phone && <a href={`tel:${it.customer_phone}`} className="text-xs text-ink-muted hover:text-brand">{it.customer_phone}</a>}
                    </td>
                    <td className="px-5 py-4 font-medium text-ink">{inr(it.total || 0)}<span className="ml-1 text-xs uppercase text-ink-muted">{it.payment_method}</span></td>
                    <td className="hidden px-5 py-4 text-ink-soft sm:table-cell">{fmt(it.no_partner_at)}</td>
                    <td className="px-5 py-4">
                      {resolutionBadge(it.resolution)}
                      {it.resolution === "self_pickup" && it.bonus > 0 && <p className="mt-1 text-[11px] text-ink-muted">Saved {inr(it.bonus)} bonus{it.fee_refund ? ` + ${inr(it.fee_refund)} fee` : ""}</p>}
                      {it.resolution === "refund" && <p className="mt-1 text-[11px] text-ink-muted">{it.auto_cancelled ? "Auto-cancelled (no decision)" : "Customer declined pickup"}{it.refund_status ? ` · ${it.refund_status}` : ""}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
